import { Fragment, useState } from "react";
import { Collapse, Drawer as MuiDrawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Toolbar, IconButton, styled } from "@mui/material";
import ProductionSheet from ".";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router";
import { ChevronLeft, ChevronRight, ExpandLess, ExpandMore } from "@mui/icons-material";
import ProductionReports from "../ProductionReports";
import { useSelector } from "react-redux";

const drawerWidth = 200;

const openedMixin = (theme) => ({
    width: drawerWidth,
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
});

const closedMixin = (theme) => ({
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: `calc(${theme.spacing(6)} + 1px)`,
    [theme.breakpoints.up('sm')]: {
        width: `calc(${theme.spacing(6)} + 1px)`,
    },
});

export function checkForProductionReportsAccess(role) {
    // Except "Treat Team" all other users can access Production Reports
    const roles = ["Treat Team"];
    return !roles.includes(role);
}

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
    ({ theme, open }) => ({
        width: drawerWidth,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
        ...(open && {
            ...openedMixin(theme),
            '& .MuiDrawer-paper': openedMixin(theme),
        }),
        ...(!open && {
            ...closedMixin(theme),
            '& .MuiDrawer-paper': closedMixin(theme),
        }),
    }),
);

const MenuOptions = [
    { label: 'Production Sheets', path: 'sheets', hasSubMenus: false, icon: 'production-sheet-icon.png', selectedIcon: 'production-sheet-white-icon.png' },
    {
        label: 'Reporting', path: 'reports', icon: 'reporting-icon.png', selectedIcon: 'reporting-white-icon.png',
        hasSubMenus: true,
        subMenus: [
            { label: 'Flavor', path: 'flavor', icon: 'flavor-icon.png', selectedIcon: 'flavor-white-icon.png' },
            // Weather Reports - Menu
            // { label: 'Weather', path: 'weather', icon: 'weather-icon.png', selectedIcon: 'weather-white-icon.png' }
        ]
    }
];

export const ProductionSheetLayout = () => {
    const navigate = useNavigate();
    const { pathname } = useLocation();

    const [openSubmenu, setOpenSubmenu] = useState(true);
    const [open, setOpen] = useState(true);
    const { profile } = useSelector((state) => state);

    const handleClick = () => {
        setOpenSubmenu(!openSubmenu);
    };

    const handleDrawer = () => {
        setOpen(!open);
    };

    const hasReportAccess = profile?.role && checkForProductionReportsAccess(profile?.role);

    return (
        <div className="mx-auto max-w-7xl  flex">
            {hasReportAccess && <Drawer
                variant="permanent" open={open}
                sx={{
                    width: drawerWidth,
                    flexShrink: 0,
                    '& .MuiDrawer-paper': {
                        // width: drawerWidth,
                        boxSizing: 'border-box',
                        overflow: 'visible',
                        left: 'auto'
                    },
                    zIndex: 299
                }}
                className="relative left-0 2xl:left-auto"
            >
                <Toolbar className="mb-5" />
                <IconButton title={open ? 'Collapse' : 'Expand'} size="small" className="absolute bg-primary text-white top-[70px] -right-2 z-10 w-5 h-5" onClick={handleDrawer}>
                    {open ? <ChevronLeft className="w-4" /> : <ChevronRight className="w-4" />}
                </IconButton>
                <List>
                    {MenuOptions.map(({ label, path, icon, selectedIcon, hasSubMenus, subMenus = [] }, index) => {
                        const isSelected = pathname.includes(path);
                        return (
                            <Fragment key={index}>
                                <ListItem disablePadding onClick={hasSubMenus ? handleClick : () => navigate(`/production/${path}`)}>
                                    <ListItemButton className="h-11" title={!open && label} classes={{ selected: "bg-primary text-white" }} selected={!hasSubMenus && isSelected}>
                                        <ListItemIcon sx={{ minWidth: 20, paddingRight: '12px' }}>
                                            <img
                                                width={20} height={20}
                                                src={`../../assets/icons/${(!hasSubMenus && isSelected) ? selectedIcon : icon}`}
                                                alt={path}
                                            />
                                        </ListItemIcon>
                                        {open && <ListItemText classes={{ primary: "text-sm font-medium" }} primary={label} />}
                                        {(hasSubMenus && open) && (openSubmenu ? <ExpandLess /> : <ExpandMore />)}
                                    </ListItemButton>
                                </ListItem>
                                {hasSubMenus && <Collapse in={openSubmenu} timeout="auto" unmountOnExit>
                                    <List component="div" disablePadding>
                                        {subMenus.map((submenu, index) => {
                                            const isSubmenuSelected = pathname.includes(submenu.path);
                                            return (
                                                <ListItemButton title={!open && submenu.label} classes={{ selected: "bg-primary text-white" }} className={`h-11 ${open && 'pl-8'}`} key={index} selected={isSubmenuSelected} onClick={() => navigate(`/production/${path}/${submenu.path}`)}>
                                                    <ListItemIcon sx={{ minWidth: 20, paddingRight: '12px' }}>
                                                        <img
                                                            width={20} height={20}
                                                            src={`../../assets/icons/${isSubmenuSelected ? submenu.selectedIcon : submenu.icon}`}
                                                            alt={path}
                                                        />
                                                    </ListItemIcon>
                                                    {open && <ListItemText classes={{ primary: "text-sm" }} primary={submenu.label} />}
                                                </ListItemButton>
                                            )
                                        })}
                                    </List>
                                </Collapse>}
                            </Fragment>
                        )
                    })}
                </List>
            </Drawer>}
            <div className="w-full">
                <Routes>
                    <Route
                        path={'sheets'}
                        element={<ProductionSheet drawerOpen={open} hasReportAccess={hasReportAccess} />}
                        key={'sheets'}
                        index
                    />
                    {hasReportAccess &&
                        <Route path={'reports'} key={'reports'}>
                            <Route path="flavor" element={<ProductionReports type="flavor" />} />
                            {/* Weather Reports Route */}
                            {/* <Route path="weather" element={<ProductionReports type="weather" />} /> */}
                        </Route>
                    }
                    <Route path="*" element={<Navigate to={`${pathname.includes('/treatteam') ? '/treatteam' : ''}/production/sheets`} />} />
                </Routes>
            </div>
        </div>
    )
}